import { AxiosError } from 'axios'
import type { ApiErrorShape } from '@/types/common'

export class ApiError extends Error implements ApiErrorShape {
  status: number
  fieldErrors?: Record<string, string[]>

  constructor(shape: ApiErrorShape) {
    super(shape.message)
    this.name = 'ApiError'
    this.status = shape.status
    this.fieldErrors = shape.fieldErrors
  }
}

export interface ErrorEnvelopeItem {
  message: string
  field?: string
}

interface ErrorBody {
  message?: string
  errors?: Record<string, string[]> | ErrorEnvelopeItem[]
}

/** Groups the backend's `errors: [{ message, field? }]` envelope into a per-field bag. */
function fromEnvelope(items: ErrorEnvelopeItem[]): {
  message?: string
  fieldErrors?: Record<string, string[]>
} {
  const fieldErrors: Record<string, string[]> = {}

  for (const item of items) {
    if (item.field) {
      fieldErrors[item.field] = [...(fieldErrors[item.field] ?? []), item.message]
    }
  }

  return {
    message: items[0]?.message,
    fieldErrors: Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined,
  }
}

/**
 * Normalizes an Axios error into an ApiError. Understands both the backend's
 * `{ errors: [{ message, field? }] }` envelope and Laravel's default
 * `{ message, errors: { field: [..] } }` validation bag.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0
    const body = error.response?.data as ErrorBody | undefined

    if (Array.isArray(body?.errors)) {
      const { message, fieldErrors } = fromEnvelope(body.errors)
      return new ApiError({ status, message: message ?? error.message, fieldErrors })
    }

    return new ApiError({
      status,
      message: body?.message ?? error.message,
      fieldErrors: body?.errors,
    })
  }

  return new ApiError({ status: 0, message: 'An unexpected error occurred.' })
}
